/* eslint-disable @typescript-eslint/no-unnecessary-condition */
/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useMemo, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { useKubeflexStore } from 'entities/kubeflex/model/useKubeflexStore';
import { apiService } from 'shared/api/apiService';
import {
	KubeflexTemplateInfo,
	KvmDiskTypeConfigurationInfos,
	KvmKubeflexConfigurationInfoShort,
} from 'shared/api/contracts';
import ReturnSVG from 'shared/assets/images/icons/return.svg';
import { getNearestNumber } from 'shared/lib/getNearestNumber';
import { Input } from 'shared/ui/Input/Input';
import { InputRange } from 'shared/ui/InputRange/InputRange';
import { LoaderBlock } from 'shared/ui/Loader/LoaderBlock';
import { NumberStepper } from 'shared/ui/NumberStepper/NumberStepper';
import { Select } from 'shared/ui/Select/Select';
import { Typography } from 'shared/ui/Typography/Typography';
import styles from './Calculator.module.scss';
import { useSmoothSlider } from './hooks/useSmoothSlider';
import { ConfiguratorFormFields } from './model/types';

const { Text } = Typography;

export const Calculator = () => {
	const { t } = useTranslation();
	const { configuration, setCurrentConfig, currentConfig } = useKubeflexStore();

	const { control, watch, setValue, register, reset } = useFormContext<ConfiguratorFormFields>();

	const [isLoading, setIsLoading] = useState(false);

	const [initialVmConfigurations, setInitialVmConfigurations] = useState<KvmKubeflexConfigurationInfoShort[]>(
		configuration?.VmConfigurations ?? [],
	);

	const initialDiskTypeConfigurationInfos = configuration?.DiskTypeConfigurationInfos || [];
	const [availableCpuList, setAvailableCpuList] = useState<KvmKubeflexConfigurationInfoShort[]>([]);
	const [availableRamList, setAvailableRamList] = useState<KvmKubeflexConfigurationInfoShort[]>([]);
	const [availableDiskTypesList, setAvailableDiskTypesList] = useState<KvmDiskTypeConfigurationInfos[]>([]);
	const [availableDiskSizeList, setAvailableDiskSizeList] = useState<KvmDiskTypeConfigurationInfos[]>([]);

	const [debouncedRam, setDebouncedRam] = useState<number | null>(null);
	const [debouncedCpu, setDebouncedCpu] = useState<number | null>(null);
	const [debouncedSize, setDebouncedSize] = useState<number | null>(null);
	const [minTemplate, setMinTemplate] = useState<KubeflexTemplateInfo | undefined>();

	const kubeflexTypeId = watch(`type`);
	const nodesType = watch(`nodesType`);
	const cpu = watch(`cpu`);
	const ram = watch(`ram`);
	const diskType = watch(`diskType`);

	const clusterTypes = useMemo(() => {
		const configurations =
			configuration?.VmConfigurations?.map(config => ({
				value: config.KubeflexTypeID ?? ``,
				label: config.KubeflexTypeName ?? ``,
			})) ?? [];

		return [
			{
				value: ``,
				label: t(`CHOOSE`),
			},
			...new Map(configurations.map(item => [item.value, item])).values(),
		];
	}, [configuration]);

	const versions = useMemo(
		() => [
			{
				value: ``,
				label: t(`CHOOSE`),
			},
			...(kubeflexTypeId
				? (configuration?.Versions?.filter(version => version.KubernetesFlexTypeId === kubeflexTypeId).map(
						item => ({
							value: item.VersionId ?? ``,
							label: item.VersionName ?? ``,
						}),
					) ?? [])
				: []),
		],
		[configuration?.Versions, kubeflexTypeId],
	);

	const availableNodeTypes = useMemo(() => {
		const uniqueVmTypeIds = [
			...new Map(
				(initialVmConfigurations.filter(item => item.KubeflexTypeID === kubeflexTypeId) ?? []).map(item => [
					item.VmTypeId,
					item.VmTypeId,
				]),
			).values(),
		];

		const filteredList =
			configuration?.VmTypes?.filter(item => uniqueVmTypeIds.includes(item.ID)).map(item => ({
				value: item.ID ?? ``,
				label: item.Name ?? ``,
			})) ?? [];

		if (filteredList[0]?.value) {
			setValue(`nodesType`, filteredList[0]?.value);
		}

		return [
			{
				value: ``,
				label: t(`CHOOSE`),
			},
			...filteredList,
		];
	}, [initialVmConfigurations, configuration?.VmTypes, kubeflexTypeId]);

	const {
		sliderValue: cpuSliderValue,
		setSliderValue: setCpuSliderValue,
		sliderProps: cpuSliderProps,
	} = useSmoothSlider(availableCpuList, debouncedCpu, `CPU`);
	const {
		sliderValue: ramSliderValue,
		setSliderValue: setRamSliderValue,
		sliderProps: ramSliderProps,
	} = useSmoothSlider(availableRamList, debouncedRam, `RAM`);

	useEffect(() => {
		const cpuList = nodesType
			? [
					...new Map(
						initialVmConfigurations
							.filter(
								item => item.KubeflexTypeID === kubeflexTypeId && item.VmTypeId === nodesType,
								// &&
								// (isTestPeriod ? item.TestPeriodDaysCount > 0 : true),
							)
							.map(item => [item.CPU, item]),
					).values(),
				].sort((a, b) => a.CPU - b.CPU)
			: [];
		setAvailableCpuList(cpuList);

		setDebouncedCpu(cpuList[0]?.CPU);
	}, [configuration?.VmConfigurations, kubeflexTypeId, nodesType]);

	useEffect(() => {
		const ramList = cpu
			? (initialVmConfigurations
					.filter(
						item =>
							item.CPU === cpu && item.KubeflexTypeID === kubeflexTypeId && item.VmTypeId === nodesType,
						// &&
						// (isTestPeriod ? item.TestPeriodDaysCount > 0 : true),
					)
					.sort((a, b) => a.RAM - b.RAM) ?? [])
			: [];

		const filterMinTemplate = minTemplate ? ramList.filter(item => item.RAM >= minTemplate?.RamMinSize) : ramList;
		setAvailableRamList(filterMinTemplate);
		setDebouncedRam(filterMinTemplate[0]?.RAM ?? 1);
	}, [cpu, minTemplate]);

	useEffect(() => {
		const getSystemDiskTypes = (vmTypeId: string) => {
			let systemDiskTypes: string[] = [];

			if (vmTypeId) {
				const vmType = (configuration?.VmTypes || []).filter(item => item.ID === vmTypeId)[0];
				if (vmType) {
					systemDiskTypes = vmType.DiskTypesForSystemDisk || [];
				}
			}

			return systemDiskTypes;
		};

		const systemDiskTypes =
			currentConfig && currentConfig.VmTypeId ? getSystemDiskTypes(currentConfig.VmTypeId) : [];

		const list = [
			...new Map(
				(minTemplate
					? initialDiskTypeConfigurationInfos.filter(
							item =>
								item.SizeFrom <= minTemplate?.SystemDiskMinSize &&
								item.SizeTo >= minTemplate?.SystemDiskMinSize &&
								systemDiskTypes.includes(item.DiskTypeId ?? ``),
						)
					: [...initialDiskTypeConfigurationInfos]
				).map(item => [item.DiskTypeId, item]),
			).values(),
		];
		setAvailableDiskTypesList(list);

		if (list[0]) {
			setValue(`diskType`, list[0]?.DiskTypeId);
		}
	}, [initialDiskTypeConfigurationInfos, configuration?.VmConfigurations, nodesType, minTemplate]);

	useEffect(() => {
		const diskSizeList = diskType
			? (initialDiskTypeConfigurationInfos
					?.filter(item => item.DiskTypeId === diskType)
					.sort((a, b) => a.SizeFrom - b.SizeFrom) ?? [])
			: [];
		setAvailableDiskSizeList(diskSizeList);
		setDebouncedSize(minTemplate?.SystemDiskMinSize ?? 1);
	}, [initialDiskTypeConfigurationInfos, diskType, minTemplate]);

	useEffect(() => {
		const currentConfigValue = availableRamList.find(item => item.RAM === ram);

		setCurrentConfig(currentConfigValue);
	}, [availableRamList, ram, setCurrentConfig]);

	useEffect(() => {
		(async () => {
			try {
				setIsLoading(true);
				const configTemplateId = initialVmConfigurations.find(
					item => item.KubeflexTypeID === kubeflexTypeId,
				)?.KubeflexNodesTemplateId;
				if (configTemplateId) {
					const templateResponse = await apiService.getTemplate(configTemplateId);
					setMinTemplate(templateResponse);
					setInitialVmConfigurations(
						configuration?.VmConfigurations?.filter(item => item.RAM >= templateResponse.RamMinSize) ?? [],
					);
				}
			} catch (error) {
				console.error(error);
			} finally {
				setIsLoading(false);
			}
		})();
	}, [configuration, kubeflexTypeId]);

	useEffect(() => {
		if (debouncedRam !== null) {
			if (!availableRamList.find(item => item.RAM === debouncedRam)) {
				setValue(
					`ram`,
					getNearestNumber(
						debouncedRam,
						availableRamList.map(item => item.RAM),
					),
				);
			} else {
				setValue(`ram`, debouncedRam);
			}
		}
	}, [debouncedRam, setValue, availableRamList]);

	useEffect(() => {
		if (debouncedCpu !== null) {
			if (!availableCpuList.find(item => item.CPU === debouncedCpu)) {
				setValue(
					`cpu`,
					getNearestNumber(
						debouncedCpu,
						availableCpuList.map(item => item.CPU),
					),
				);
			} else {
				setValue(`cpu`, debouncedCpu);
			}
		}
	}, [debouncedCpu, setValue, availableRamList]);

	useEffect(() => {
		if (debouncedSize !== null) {
			if (debouncedSize < (minTemplate?.SystemDiskMinSize ?? 1)) {
				setValue(`size`, minTemplate?.SystemDiskMinSize ?? 1);
			} else if (debouncedSize > (availableDiskSizeList[availableDiskSizeList.length - 1]?.SizeTo ?? 1)) {
				setValue(`size`, availableDiskSizeList[availableDiskSizeList.length - 1]?.SizeTo ?? 1);
			} else {
				setValue(`size`, debouncedSize);
			}
		}
	}, [debouncedSize, setValue, availableRamList]);

	useEffect(() => {
		setInitialVmConfigurations(configuration?.VmConfigurations ?? []);
	}, [configuration]);

	return (
		<div className={styles.container}>
			<LoaderBlock isLoading={isLoading} relativeToParent>
				<div className={styles.block}>
					<div className={styles.inputsWrapper}>
						<Select
							options={clusterTypes}
							placeholder={t(`CHOOSE`)}
							control={control}
							label={t(`CLUSTER_TYPE`)}
							name='type'
							tooltipMessage={
								configuration?.VmConfigurations?.find(item => item.KubeflexTypeID === kubeflexTypeId)
									?.KubeflexTypeNote
							}
							onSelect={() => setValue(`version`, ``)}
							errorMessage={t(`REQUIRED_FIELD`)}
							required
						/>
						<Select
							options={versions}
							placeholder={t(`CHOOSE`)}
							control={control}
							label={t(`KUBERNETES_VERSION`)}
							name='version'
							required
							errorMessage={t(`REQUIRED_FIELD`)}
						/>
					</div>
				</div>
				<div className={styles.block}>
					<div className={styles.inputsWrapper}>
						<Select
							options={availableNodeTypes}
							placeholder={t(`CHOOSE`)}
							control={control}
							label={t(`TYPE_OF_UNITS`)}
							name='nodesType'
							tooltipMessage={
								initialVmConfigurations.find(item => item.VmTypeId === nodesType)?.VmTypeNote
							}
						/>
						<NumberStepper
							{...register(`nodes`, {
								valueAsNumber: true,
							})}
							label={t(`NUMBER_OF_NODES`)}
							min={1}
							max={10}
						/>
					</div>
					<div className={styles[`form-content__block`]}>
						<Input
							{...register(`cpu`)}
							label='CPU'
							disabled={!nodesType}
							onChange={e => {
								setDebouncedCpu(Number(e.target.value));
							}}
						/>
						<InputRange
							id='cpu'
							{...cpuSliderProps}
							value={cpuSliderValue}
							step={1}
							onChange={event => {
								setCpuSliderValue(Number(event.currentTarget.value));
								setDebouncedCpu(
									availableCpuList[Math.round(Number(event.currentTarget.value) / 100)].CPU,
								);
							}}
							onMouseUp={event => {
								setCpuSliderValue(Math.round(Number(event.currentTarget.value) / 100) * 100);
								setDebouncedCpu(
									availableCpuList[Math.round(Number(event.currentTarget.value) / 100)].CPU,
								);
							}}
							disabled={!nodesType || availableCpuList.length <= 1}
						/>

						<Input
							{...register(`ram`)}
							label='RAM'
							disabled={!cpu}
							onChange={e => {
								setDebouncedRam(Number(e.target.value));
							}}
						/>
						<InputRange
							id='ram'
							{...ramSliderProps}
							value={ramSliderValue}
							step={1}
							onChange={event => {
								setRamSliderValue(Number(event.currentTarget.value));
								setDebouncedRam(
									availableRamList[Math.round(Number(event.currentTarget.value) / 100)].RAM,
								);
							}}
							onMouseUp={event => {
								setRamSliderValue(Math.round(Number(event.currentTarget.value) / 100) * 100);
								setDebouncedRam(
									availableRamList[Math.round(Number(event.currentTarget.value) / 100)].RAM,
								);
							}}
							dataType={t(`GB`)}
							disabled={!cpu || availableRamList.length <= 1}
						/>
					</div>
				</div>
				<div className={styles.block}>
					<div className={styles.inputsWrapper}>
						<Select
							options={[
								...availableDiskTypesList.map(item => ({
									value: item.DiskTypeId ?? ``,
									label: item.DiskTypeName ?? ``,
								})),
							]}
							placeholder={t(`CHOOSE`)}
							control={control}
							label={t(`DISC_TYPE`)}
							name='diskType'
							{...(availableDiskTypesList.length !== 0
								? {
										tooltipMessage: availableDiskTypesList.filter(
											item => item.DiskTypeId === diskType,
										)[0]?.DiskTypeNote,
									}
								: {})}
							onSelect={() => {
								setValue(`size`, availableDiskSizeList[0].SizeFrom);
							}}
							disabled={!ram}
						/>
						<div className={styles[`form-content__block`]}>
							<Input
								label={t(`DISK_SIZE`)}
								{...register(`size`)}
								onChange={e => {
									setDebouncedSize(Number(e.target.value));
								}}
								disabled={!diskType}
							/>
							<InputRange
								id='size'
								min={minTemplate?.SystemDiskMinSize ?? 1}
								max={availableDiskSizeList[availableDiskSizeList.length - 1]?.SizeTo ?? 1}
								value={debouncedSize ?? 0}
								step={1}
								onChange={event => setDebouncedSize(Number(event.target.value))}
								dataType={t(`GB`)}
								disabled={!nodesType || !cpu || !ram || !diskType}
							/>
						</div>
					</div>
				</div>
				<div className={styles[`form__button-block`]}>
					<div />
					<button
						type='button'
						onClick={() => {
							reset({
								version: ``,
								groupName: ``,
								nodes: 0,
								nodesType: ``,
								type: ``,
								size: 0,
								cpu: 0,
								ram: 0,
								name: ``,
								description: ``,
								virtualNetwork: ``,
								region: ``,
								dataCenter: ``,
							});
							setCurrentConfig(undefined);
						}}
					>
						<Text variant='button-md' as='div' className={styles[`form-content__button`]}>
							{t(`DEFAULT_VALUES`)} <ReturnSVG />
						</Text>
					</button>
				</div>
			</LoaderBlock>
		</div>
	);
};
